import React, { useCallback, useEffect, useRef, useState } from "react";
import * as cornerstone from "cornerstone-core";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import dicomParser from "dicom-parser";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

// Initialize cornerstone
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

const MOBILE_MAX_WIDTH = "959px";
const DESKTOP_MIN_WIDTH = "960px";

const CloseButton = ({ handleCloseModal3, colors }) => (
  <IconButton
    onClick={handleCloseModal3}
    sx={{
      position: "absolute",
      //top: 8,
      right: 8,
      width: "32px",
      height: "32px",
      bgcolor: colors.background,
      color: colors.color,
      borderRadius: 0,
      "&:hover": {
        bgcolor: colors.hoverBackground,
      },
      boxShadow: "none",
      [`@media (max-width: ${MOBILE_MAX_WIDTH})`]: {
        bgcolor: colors.background,
        color: colors.color,
      },
    }}
  >
    <CloseIcon />
  </IconButton>
);
const DicomViewer = ({ base64Images, handleCloseModal3 }) => {
  const { config } = useSelector((state) => state.user);
  const elementRef = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [imageCount, setImageCount] = useState(0);
  const isMobile = useMediaQuery("(max-width: 960px)");
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showSideBySide, setShowSideBySide] = useState(false);
  const viewerConfig =
    window.innerWidth < 960
      ? config.pdfViewer.mobile
      : config.pdfViewer.desktop;

  const sideBySideRef1 = useRef(null);
  const sideBySideRef2 = useRef(null);
  // const handleImageRendered = (event) => {
  //   console.log("Image rendered:", event);
  // };

  const safelyEnableCornerstone = (element) => {
    if (!element) return false;

    try {
      // Check if already enabled
      if (element.hasAttribute("data-cornerstone-enabled")) {
        return true;
      }

      cornerstone.enable(element);

      return true;
    } catch (err) {
      console.error("Error enabling cornerstone:", err);
      return false;
    }
  };

  const safelyDisableCornerstone = (element) => {
    if (!element) return;

    try {
      // Only try to disable if it was previously enabled
      if (element.hasAttribute("data-cornerstone-enabled")) {
        cornerstone.disable(element);
      }
    } catch (err) {
      console.error("Error disabling cornerstone:", err);
    }
  };

  // useEffect(() => {
  //   // Reset refs array when toggling side-by-side view or when images change
  //   if (showSideBySide) {
  //     sideBySideRef.current = Array(Math.min(2, base64Images.length))
  //       .fill()
  //       .map((_, i) => sideBySideRef.current[i] || React.createRef());
  //   }
  // }, [showSideBySide, base64Images]);

  useEffect(() => {
    if (
      !base64Images ||
      !Array.isArray(base64Images) ||
      base64Images.length === 0
    ) {
      setError("No DICOM images available");
      return;
    }
    setImageCount(base64Images.length);
  }, [base64Images]);

  useEffect(() => {
    if (showSideBySide || !elementRef.current) return;
    const element = elementRef.current;
    if (!element) return;

    // Display the current image
    const displayCurrentImage = async () => {
      try {
        // Enable cornerstone on the element
        if (!safelyEnableCornerstone(element)) {
          setError("Failed to initialize DICOM viewer");
          return;
        }

        const currentImage = base64Images[currentImageIndex];
        if (!currentImage || !currentImage.FilePacs) {
          setError("Invalid image data");
          return;
        }

        const base64String = currentImage.FilePacs;
        // Remove any data URL prefix if present
        const base64Data = base64String.includes(",")
          ? base64String.split(",")[1]
          : base64String;

        // Convert base64 to binary
        const binaryString = window.atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create a proper Blob with the correct MIME type
        const blob = new Blob([bytes.buffer], { type: "application/dicom" });

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Use the wadouri loader
        const imageId = `wadouri:${url}`;

        // Load and display the image
        const image = await cornerstone.loadImage(imageId);
        cornerstone.displayImage(element, image);
      } catch (err) {
        console.error("Error displaying DICOM image:", err);
        setError(`Failed to display DICOM image: ${err.message}`);
      }
    };

    displayCurrentImage();
  }, [base64Images, currentImageIndex, showSideBySide]);

  useEffect(() => {
    if (!showSideBySide || !base64Images || base64Images.length === 0) return;

    const element1 = sideBySideRef1.current;
    const element2 = sideBySideRef2.current;
    // Display the first two images side by side
    const displaySideBySideImages = async () => {
      // Handle first image
      if (element1) {
        try {
          // Enable cornerstone on the element
          if (!safelyEnableCornerstone(element1)) {
            console.error(
              "Failed to initialize DICOM viewer for first side-by-side image"
            );
          } else {
            // Process and display the first image
            await displayImageOnElement(base64Images[0], element1);
          }
        } catch (err) {
          console.error(
            "Error displaying first side-by-side DICOM image:",
            err
          );
        }
      }

      // Handle second image if it exists
      if (element2 && base64Images.length > 1) {
        try {
          // Enable cornerstone on the element
          if (!safelyEnableCornerstone(element2)) {
            console.error(
              "Failed to initialize DICOM viewer for second side-by-side image"
            );
          } else {
            // Process and display the second image
            await displayImageOnElement(base64Images[1], element2);
          }
        } catch (err) {
          console.error(
            "Error displaying second side-by-side DICOM image:",
            err
          );
        }
      }
    };

    // Helper function to display an image on an element
    const displayImageOnElement = async (imageData, element) => {
      if (!imageData || !imageData.FilePacs) {
        console.error("Invalid image data");
        return;
      }

      // Process the image (same as in the single view)
      const base64String = imageData.FilePacs;
      const base64Data = base64String.includes(",")
        ? base64String.split(",")[1]
        : base64String;

      const binaryString = window.atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let j = 0; j < binaryString.length; j++) {
        bytes[j] = binaryString.charCodeAt(j);
      }

      const blob = new Blob([bytes.buffer], { type: "application/dicom" });
      const url = URL.createObjectURL(blob);
      const imageId = `wadouri:${url}`;

      // Load and display the image
      const image = await cornerstone.loadImage(imageId);
      cornerstone.displayImage(element, image);
    };
    displaySideBySideImages();

    // Clean up function
    return () => {
      if (element1) {
        safelyDisableCornerstone(element1);
      }
      if (element2) {
        safelyDisableCornerstone(element2);
      }
    };
  }, [showSideBySide, base64Images]);

  // Clean up when component unmounts
  useEffect(() => {
    const element = elementRef.current;
    return () => {
      if (element) {
        safelyDisableCornerstone(element);
      }
    };
  }, []);
  const handleNextImage = () => {
    if (currentImageIndex < imageCount - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };
  const handleDownload = () => {
    const link = document.createElement("a");
    const currentImage = base64Images[currentImageIndex];
    const base64Data = currentImage.FilePacs;
    const fileName = `dicom-image-${currentImageIndex}.dcm`;
    link.href = `data:application/dicom;base64,${base64Data}`;
    link.download = fileName;
    link.click();
  };
  // Memoize the event handlers with useCallback
  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      const zoomIntensity = 0.1;
      const newScale =
        scale + (event.deltaY > 0 ? -zoomIntensity : zoomIntensity);
      setScale(Math.max(newScale, 0.5)); // prevent scaling below 0.5
    },
    [scale]
  );

  const handleMouseDown = useCallback(
    (event) => {
      const startPosition = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
      const onMouseMove = (moveEvent) => {
        setPosition({
          x: moveEvent.clientX - startPosition.x,
          y: moveEvent.clientY - startPosition.y,
        });
      };
      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [position]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (element) {
      element.addEventListener("wheel", handleWheel);
      element.addEventListener("mousedown", handleMouseDown);
    }
    return () => {
      if (element) {
        element.removeEventListener("wheel", handleWheel);
        element.removeEventListener("mousedown", handleMouseDown);
      }
    };
  }, [handleWheel, handleMouseDown, showSideBySide]);

  const toggleSideBySide = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setShowSideBySide((prev) => !prev);
  };
  if (error) {
    return (
      <div style={{ color: "red", padding: "20px", textAlign: "center" }}>
        {error}
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <CloseButton
        handleCloseModal3={handleCloseModal3}
        colors={viewerConfig.closeButton}
      />
      {/* Button to toggle side-by-side view */}
      <button onClick={toggleSideBySide} style={{ marginBottom: "10px" }}>
        {showSideBySide ? "Show Single Image" : "Show Images Side by Side"}
      </button>

      {!showSideBySide && (
        <div
          ref={elementRef}
          style={{
            width: "512px",
            height: "512px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#000",
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
          }}
        ></div>
      )}

      {showSideBySide && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <div
            ref={sideBySideRef1}
            style={{
              width: "512px",
              height: "512px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "#000",
            }}
          ></div>
          {base64Images.length > 1 && (
            <div
              ref={sideBySideRef2}
              style={{
                width: "512px",
                height: "512px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#000",
              }}
            ></div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <button
          onClick={handlePreviousImage}
          disabled={currentImageIndex === 0 || imageCount === 0}
          style={{
            padding: "8px 16px",
            backgroundColor: currentImageIndex === 0 ? "#ccc" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: currentImageIndex === 0 ? "not-allowed" : "pointer",
          }}
        >
          Previous
        </button>
        <span style={{ margin: "0 10px" }}>
          {imageCount > 0
            ? `${currentImageIndex + 1} / ${imageCount}`
            : "No images"}
        </span>
        <button
          onClick={handleNextImage}
          disabled={currentImageIndex === imageCount - 1 || imageCount === 0}
          style={{
            padding: "8px 16px",
            backgroundColor:
              currentImageIndex === imageCount - 1 ? "#ccc" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              currentImageIndex === imageCount - 1 ? "not-allowed" : "pointer",
          }}
        >
          Next
        </button>
        <button onClick={handleDownload} style={{ marginTop: "10px" }}>
          Download Image
        </button>
      </div>
    </div>
  );
};

export default DicomViewer;
